import type {
  HostCampaignConfig,
  ProcessLaunchPackageAndroid,
} from '@playcastdotio/Messaging';
import type { InstanceCapabilities } from '../../../services/playjector/interfaces/IPlayjectorService';
import { resolveEffectiveMaxPlayers } from '../../game-instance/effectiveMaxPlayers';

/**
 * Android multi-instance launch package build (android-multi-instance-host
 * Phase 3).
 *
 * Pure functions that turn a campaign config into the single
 * `ProcessLaunchPackageAndroid` the host sends per campaign launch. The
 * package carries game + clamped `instanceCount` only — all
 * monitor/display/vmId fields are omitted because the machine owns that
 * lifecycle (C++ `androidLaunchPackage` schema is strict about this).
 * Playjector never installs at launch; provisioning warming is the only
 * install path.
 */

export type AndroidLaunchPackageInputs = {
  campaign: HostCampaignConfig;
  streamId: string;
  /** Ops-controlled maxPlayers setting (hostCapacityConfigState). */
  configMaxPlayers: number;
  /**
   * DEPRECATED / accepted-but-ignored: the gaming-score hardware tier no longer
   * clamps capacity. `maxPlayers` + the reported ceiling are authoritative.
   * Retained so the launch call site need not change.
   */
  hardwareCap: number | undefined;
  /** Machine-reported capabilities; `undefined` = unknown (ceiling ignored). */
  capabilities: Pick<InstanceCapabilities, 'maxGameInstances'> | undefined;
};

/**
 * Deterministic, campaign-scoped streamId with a monotonic per-launch suffix:
 * `android-{campaignId}-{n}`. Deterministic so logs/reports correlate a stream
 * with its campaign; suffixed so a relaunch never reuses a live registration
 * (teardown unregisters the previous id).
 */
export const mintAndroidStreamId = (campaignId: string, launchSequence: number): string =>
  `android-${campaignId}-${launchSequence}`;

/**
 * Phase 2 clamp applied to the campaign's request:
 * `min(gameInstancesPerHost ?? 1, configMaxPlayers, reportedMaxGameInstances)`.
 * The gaming-score hardware tier no longer clamps (advisory only), so
 * `hardwareCap` is accepted-but-ignored. Unknown inputs are no-ops
 * (single-instance fallback preserved); a reported ceiling of 0 is a real value
 * and clamps to 0.
 */
export const resolveAndroidInstanceCount = (
  gameInstancesPerHost: number | undefined,
  configMaxPlayers: number,
  hardwareCap: number | undefined,
  reportedMaxGameInstances: number | undefined,
): number =>
  Math.min(
    gameInstancesPerHost ?? 1,
    resolveEffectiveMaxPlayers(configMaxPlayers, hardwareCap, reportedMaxGameInstances),
  );

/**
 * Default `readyWaitTimeMs` when the campaign omits it. MUST match the C++
 * default in `SchemaList.cpp` (androidLaunchPackage schema, ~line 1161:
 * 30000) — defaulting to 0 here would defeat the C++-side ready wait.
 */
export const DEFAULT_ANDROID_READY_WAIT_TIME_MS = 30_000;

/**
 * Build the android launch package for one campaign launch.
 *
 * Throws (fails the launch fast, with an actionable message) when the campaign
 * is not launchable as android:
 *  - the launch package carries no APK `package` name;
 *  - the clamp resolves to 0 (e.g. the machine reported `maxGameInstances: 0`).
 *
 * Playjector never installs at launch (production-readiness Phase 2; the C++
 * removed apkSource from the launch contract — a cold instance fails as
 * "package not installed", `AndroidLauncher.cpp:371`). VMs must be warmed by
 * provisioning before assignment.
 */
export function buildAndroidLaunchPackage(
  inputs: AndroidLaunchPackageInputs,
): ProcessLaunchPackageAndroid {
  const { campaign, streamId } = inputs;
  const launchPackage = campaign.launchPackage as {
    package?: string;
    readyWaitTimeMs?: number;
  };

  const packageName = launchPackage.package;
  if (!packageName) {
    throw new Error(
      `Android campaign "${campaign.campaignId}" cannot launch: launchPackage is missing ` +
      '"package" (the APK package name). Re-publish the campaign with ' +
      'launchPackage.package set.',
    );
  }

  const instanceCount = resolveAndroidInstanceCount(
    campaign.gameInstancesPerHost,
    inputs.configMaxPlayers,
    inputs.hardwareCap,
    inputs.capabilities?.maxGameInstances,
  );
  if (instanceCount < 1) {
    throw new Error(
      `Android campaign "${campaign.campaignId}" cannot launch: instance count clamps to 0 ` +
      `(requested ${campaign.gameInstancesPerHost ?? 1}, machine reported maxGameInstances ` +
      `${inputs.capabilities?.maxGameInstances ?? 'unknown'}). This machine cannot run any ` +
      'game instance — reprovision it or assign the campaign elsewhere.',
    );
  }

  return {
    launcher: 'android',
    package: packageName,
    streamId,
    instanceCount,
    readyWaitTimeMs: launchPackage.readyWaitTimeMs ?? DEFAULT_ANDROID_READY_WAIT_TIME_MS,
  };
}
